import { useGame } from "~/app";
import { ActionSlot, Card, IntrigueCard, TechTile } from "~/business";
import { commandBuilderFactory } from "~/business/commandBuilderFactory";
import { AudioPaths, soundPlayer } from "~/infra";
import { useSelection } from "../context/SelectionContext";
import { useViewingPlayerOpt } from "./useViewingPlayer";

export const useActions = () => {
  const viewingPlayer = useViewingPlayerOpt();
  const viewingPlayerId = viewingPlayer?.id;
  const { game, update } = useGame();
  const {
    actionSlot: selectedActionSlot,
    selectActionSlot,
    clear,
  } = useSelection();

  const commandBuilderObj = commandBuilderFactory();

  const onActionSlotClickFactory = (actionSlot: ActionSlot) => {
    if (!viewingPlayerId) return;
    const errorOpt = game.validateActionSlotPlay(
      viewingPlayerId,
      actionSlot.id
    );
    if (errorOpt) return;
    return () => selectActionSlot(actionSlot);
  };

  const onCardClickFactory = (card: Card) => {
    if (!selectedActionSlot || !viewingPlayerId) {
      return;
    }
    const errorOpt = game.validatePlay(
      viewingPlayerId,
      card.id,
      selectedActionSlot.id
    );
    if (errorOpt) {
      return;
    }
    return () => {
      clear();
      soundPlayer.play(AudioPaths.chipLay);
      soundPlayer.play(AudioPaths.cardSlide);
      update(
        commandBuilderObj.play({
          cardId: card.id,
          actionSlotId: selectedActionSlot.id,
        })
      );
    };
  };

  const onShopCardClickFactory = (card: Card) => {
    if (!viewingPlayerId) return;
    const errorOpt = game.validateBuyCard(viewingPlayerId, card.id);
    if (errorOpt) {
      return;
    }
    return () => {
      soundPlayer.play(AudioPaths.cardSlide);
      update(commandBuilderObj.buyCard(card.id));
    };
  };

  const onReveal =
    !viewingPlayerId || game.validateRevealAll(viewingPlayerId)
      ? undefined
      : () => {
          update(commandBuilderObj.revealAll());
        };

  const onEndTurn =
    !viewingPlayerId || game.validateEndBuyPhase(viewingPlayerId)
      ? undefined
      : () => {
          update(commandBuilderObj.endBuyPhase());
        };

  const onPassCombatIntrigue =
    !viewingPlayerId || game.validatePassIntrigue(viewingPlayerId)
      ? undefined
      : () => update(commandBuilderObj.passIntrigue());

  const onIntrigue = (card: IntrigueCard) =>
    !viewingPlayerId || game.validatePlayIntrigue(viewingPlayerId, card.id)
      ? undefined
      : update(commandBuilderObj.playIntrigue(card.id));

  const onPlayGlobalEffect =
    !viewingPlayerId || game.validatePlayGlobalEffect(viewingPlayerId)
      ? undefined
      : () => update(commandBuilderObj.playGlobalEffect());

  const onUseTech = (tech: TechTile) =>
    !viewingPlayerId || game.validateUseTech(viewingPlayerId, tech.id)
      ? undefined
      : () => update(commandBuilderObj.useTech(tech.id));

  return {
    onActionSlotClickFactory,
    onCardClickFactory,
    onShopCardClickFactory,
    onReveal,
    onEndTurn,
    onPassCombatIntrigue,
    onIntrigue,
    onPlayGlobalEffect,
    onUseTech,
  };
};
